import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { Empty, Row, Skeleton } from 'antd';

import { Utils } from '@/utils';
import { Drawer } from '@/components/ui/Drawer';
import { Refetch } from '@/components/ui/Refetch';
import { PaginationFooter } from '@/components/PaginationFooter';
import { ModalDelete } from '@/components/ui/Modals/ModalDelete';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useDeleteBeraTlid, useGetBeraSummaryResultV2 } from '@/hooks';
import { FIRST_PAGE, TLID_PAGE_SIZE } from '@/views/Bera/constants';

import type { BeraJobSummaryListItem, BeraTaskResultV2 } from '@/types/Bera';
import { ClipItem } from './ClipItem';
import * as S from './styles';

interface DrawerTlidsProps {
	open: boolean;
	report: BeraJobSummaryListItem;
	summaryId: string;
	versionTitle: string | null;
	onClose: () => void;
}

export function DrawerTlids({
	open,
	report,
	summaryId,
	versionTitle,
	onClose
}: Readonly<DrawerTlidsProps>): JSX.Element {
	const { organization, company } = useApplicationContext();

	const [page, setPage] = useState<number>(FIRST_PAGE);
	const [taskToDelete, setTaskToDelete] = useState<BeraTaskResultV2 | null>(null);

	const summaryParams = { id: summaryId, organization_id: organization?.id, company_id: company?.id };
	const { data, isInitialLoading, isError, refetch } = useGetBeraSummaryResultV2(summaryParams);
	const { mutate: deleteReport, isLoading: isDeleting } = useDeleteBeraTlid();

	const tasks = data?.tasks ?? [];
	const total = tasks.length;
	const firstIndex = (page - FIRST_PAGE) * TLID_PAGE_SIZE;
	const visibleTasks = tasks.slice(firstIndex, firstIndex + TLID_PAGE_SIZE);

	const title = `${report.report_name} (${total} ${I18n.get('TLID')})`;

	function handlePageChange(nextPage: number) {
		setPage(nextPage);
	}

	function handleRowsPerPageChange() {
		setPage(FIRST_PAGE);
	}

	function handleDownload(task: BeraTaskResultV2) {
		Utils.downloadFile(task.file?.url ?? '', task.file?.original_name);
	}

	function handleOpenDeleteModal(task: BeraTaskResultV2) {
		setTaskToDelete(task);
	}

	function handleCloseDeleteModal() {
		setTaskToDelete(null);
	}

	function handleConfirmDelete() {
		const payload = {
			id: taskToDelete?.bera_report_id ?? '',
			organization_id: organization?.id,
			company_id: company?.id
		};

		deleteReport(payload, {
			onSuccess: () => {
				setPage(FIRST_PAGE);
				handleCloseDeleteModal();
			}
		});
	}

	function renderContent(): JSX.Element {
		if (isInitialLoading) {
			return <Skeleton active paragraph={{ rows: 10 }} />;
		}

		if (isError) {
			return <Refetch message="Failed to load the TLIDs." onClick={refetch} />;
		}

		if (total === 0) {
			return <Empty description={I18n.get('No TLID found')} />;
		}

		return (
			<>
				<S.ClipListArea>
					<Row>
						{!!versionTitle && <S.VersionTitle span={24}>{versionTitle}</S.VersionTitle>}
						{visibleTasks.map((task) => (
							<ClipItem
								task={task}
								key={task.bera_report_id}
								onDelete={handleOpenDeleteModal}
								onDownload={handleDownload}
							/>
						))}
					</Row>
				</S.ClipListArea>
				<PaginationFooter
					page={page}
					total={total}
					showSizeChanger={false}
					pageSize={TLID_PAGE_SIZE}
					onPageChange={handlePageChange}
					onPageSizeChange={handleRowsPerPageChange}
				/>
			</>
		);
	}

	return (
		<>
			<Drawer open={open} title={title} onClose={onClose}>
				{renderContent()}
			</Drawer>
			<ModalDelete
				open={!!taskToDelete}
				loading={isDeleting}
				onCancel={handleCloseDeleteModal}
				onConfirm={handleConfirmDelete}
				description={I18n.get('Heads up! Want to delete this TLID?')}
			/>
		</>
	);
}
